import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon, Kbd, StopSplitButton } from '@/components/ui';
import { ModeSwitch } from './ModeSwitch';
import { CommandPalette } from './CommandPalette';
import { api } from '@/lib/api';
import { Lab } from '@/lib/toast';

export interface TopBarProps {
  title?: string;
  subtitle?: string;
  onLaunch?: () => void;
}

/**
 * Top bar. Left = page title + short description. Right = four
 * visually-harmonized controls (all 32 px tall) :
 *   1. Search stub (opens CommandPalette on click or ⌘K)
 *   2. Sim/Réel switch
 *   3. Stop split-button (only while any run is active)
 *   4. Launch button (accent)
 *
 * The global stop control is ALWAYS surfaced when at least one campagne is
 * running, regardless of the current route — operators can abort from any
 * page without having to navigate to Live first. Clicking the primary action
 * stops the live currently targeted by the URL if we are on /live, otherwise
 * jumps to Live first ; the chevron exposes the platform-wide kill switch.
 */
export function TopBar({ title, subtitle, onLaunch }: TopBarProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', 'topbar-stop'],
    queryFn: () => api.listRuns({ limit: 30 }),
    refetchInterval: 2_000,  // faster detection for the emergency STOP button
  });
  const activeRuns = runs.filter((r) => r.status === 'running' || r.status === 'stopping');
  const activeCount = activeRuns.length;
  const currentLiveRun = location.pathname.startsWith('/live')
    ? activeRuns[0]?.run_id
    : undefined;

  const emergencyStopAll = async () => {
    const ok = await Lab.confirm(
      `Arrêt d'urgence : ${activeCount} campagne${activeCount > 1 ? 's ser' : ' ser'}${activeCount > 1 ? 'ont' : 'a'} tuée${activeCount > 1 ? 's' : ''} immédiatement. Confirmer ?`,
      "STOP D'URGENCE",
      { danger: true },
    );
    if (!ok) return;
    try {
      const res = await api.stopAllRuns('hard');
      Lab.toast(
        `${res.count} campagne${res.count > 1 ? 's' : ''} arrêtée${res.count > 1 ? 's' : ''} (kill immédiat).`,
        'pass',
      );
      invalidate();
    } catch (e) {
      Lab.toast(`Échec du stop d'urgence : ${(e as Error).message}`, 'error');
    }
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['runs'] });
  };

  const stopCurrent = async (mode: 'safe' | 'hard') => {
    // If we are on /live the Live page owns the stop action — don't double-fire.
    // Otherwise stop the most recent active run and navigate there so the user
    // watches the shutdown progression.
    const target = activeRuns[0];
    if (!target) return;
    const label = mode === 'safe' ? 'progressif' : 'immédiat';
    const ok = await Lab.confirm(
      `Arrêter la campagne ${target.run_id.slice(-8)} (arrêt ${label}) ?`,
      'Arrêter la campagne',
      { danger: mode === 'hard' },
    );
    if (!ok) return;
    try {
      await api.stopRun(target.run_id, mode);
      Lab.toast(`Campagne arrêtée (${mode}).`, 'pass');
      invalidate();
      if (!location.pathname.startsWith('/live')) {
        navigate(`/live?run=${target.run_id}`);
      }
    } catch (e) {
      Lab.toast(`Échec de l'arrêt : ${(e as Error).message}`, 'error');
    }
  };

  const stopAll = async (mode: 'safe' | 'hard') => {
    const label = mode === 'safe' ? 'progressif' : 'immédiat';
    const ok = await Lab.confirm(
      `Arrêter ${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours sur toute la plateforme (arrêt ${label}) ?`,
      'Arrêt global',
      { danger: mode === 'hard' },
    );
    if (!ok) return;
    try {
      const res = await api.stopAllRuns(mode);
      Lab.toast(`${res.count} campagne${res.count > 1 ? 's' : ''} arrêtée${res.count > 1 ? 's' : ''} (${mode}).`, 'pass');
      invalidate();
    } catch (e) {
      Lab.toast(`Échec de l'arrêt global : ${(e as Error).message}`, 'error');
    }
  };

  return (
    <>
      <div className="topbar">
        {/* Left : page title + description on a single line. */}
        <div className="h-row" style={{ gap: 10, minWidth: 0, alignItems: 'baseline' }}>
          {title && <span className="page-title">{title}</span>}
          {subtitle && (
            <>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }} aria-hidden>·</span>
              <span className="page-sub" style={{ marginTop: 0 }}>{subtitle}</span>
            </>
          )}
          {activeCount > 0 && (
            <span
              className="badge run mono"
              style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11 }}
              title={currentLiveRun ?? `${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours`}
            >
              {activeCount} live
            </span>
          )}
        </div>

        <div className="sp" />

        {/* Right : four harmonized controls. */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          title="Rechercher (⌘ K)"
          aria-label="Rechercher"
          className="ctrl"
          style={{
            minWidth: 0,
            paddingLeft: 10,
            paddingRight: 4,
            gap: 8,
            color: 'var(--text-muted)',
          }}
        >
          <Icon name="search" size={14} />
          <span style={{ display: 'inline-flex', gap: 2 }}>
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>

        <ModeSwitch />

        {activeCount > 0 && (
          <>
            {/* Emergency STOP — big red, kills every active campagne in one click. */}
            <button
              type="button"
              onClick={emergencyStopAll}
              title={`Stop d'urgence — tuer les ${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours`}
              aria-label="Stop d'urgence"
              className="lab-emergency-stop"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--danger-strong)',
                background: 'var(--danger)',
                color: 'var(--danger-fg)',
                fontWeight: 'var(--weight-bold)',
                fontSize: 'var(--text-sm)',
                letterSpacing: 'var(--tracking-wide)',
                textTransform: 'uppercase',
                cursor: 'pointer',
                boxShadow: '0 0 0 0 color-mix(in oklab, var(--danger) 45%, transparent)',
                animation: 'lab-emergency-pulse 1.6s ease-in-out infinite',
                transition: 'transform var(--dur-fast) var(--ease-out)',
              }}
              onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.97)'; }}
              onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            >
              <Icon name="stop" size={14} />
              <span>Stop tout</span>
              <span
                className="mono"
                style={{
                  fontSize: 10,
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-full)',
                  background: 'color-mix(in oklab, var(--danger-fg) 25%, transparent)',
                  color: 'var(--danger-fg)',
                }}
              >
                {activeCount}
              </span>
            </button>
            <StopSplitButton
              size="sm"
              label={location.pathname.startsWith('/live') ? 'Arrêter' : 'Arrêter le live'}
              onStopCurrent={stopCurrent}
              onStopAll={stopAll}
              activeCount={activeCount}
            />
          </>
        )}

        {onLaunch && (
          <button type="button" onClick={onLaunch} className="ctrl primary">
            <Icon name="play" size={12} />
            <span>Lancer</span>
          </button>
        )}
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}
